import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Api } from "../lib/api";

/**
 * ChatWidget — a persistent, site-wide help assistant. A floating launcher sits in the
 * bottom-right corner of every authenticated page; clicking it opens a chat panel that can
 *  - answer quick, non-CUI questions via the portal assistant (`api.askAi`, decoder-ring
 *    masked server-side), and
 *  - walk the user through opening a support case (`api.createTicket`) with a short guided
 *    prompt (subject -> description) without leaving the current page.
 *
 * Everything here is best-effort and client-driven: no new server surface is required. The
 * intent helpers are pure and unit-tested. §AI
 */

/** A single line in the transcript. `ticketId` marks a bot message that links to a case. */
interface ChatMessage {
  id: number;
  role: "user" | "bot";
  text: string;
  ticketId?: string;
}

/** Where we are in the (optional) guided case-creation flow. */
type Flow =
  | { kind: "chat" }
  | { kind: "await_subject" }
  | { kind: "await_description"; subject: string };

const CASE_INTENT_RE =
  /\b(?:create|open|start|file|log|raise|submit|new)\s+(?:a\s+|an\s+|new\s+)?(?:case|ticket|support\s+request)\b/i;

/** True when free-text is asking to open a support case/ticket. */
export function detectCreateCaseIntent(text: string): boolean {
  return CASE_INTENT_RE.test(text);
}

/**
 * Pull a usable case subject out of an intent phrase: "open a ticket about X" / "create a
 * case for X" / "... regarding X" -> "X". Returns "" when the message is only the intent
 * (e.g. "create a case"), so the widget knows to ask for a subject.
 */
export function extractCaseSubject(text: string): string {
  const stripped = text
    .replace(CASE_INTENT_RE, " ")
    .replace(/^\s*(?:please\s+)?/i, " ")
    .replace(/^\s*(?:about|for|regarding|re|on|to|with|that|because|—|-|:)\s+/i, " ")
    .replace(/\s+/g, " ")
    .trim();
  return stripped;
}

let nextId = 1;
function msg(role: ChatMessage["role"], text: string, ticketId?: string): ChatMessage {
  return { id: nextId++, role, text, ticketId };
}

const GREETING =
  "Hi! I'm the PumaMesh assistant. Ask me a quick question about the portal, builds, or " +
  "documentation — or tap “Create a case” and I'll help you open a support ticket.";

export function ChatWidget({ api }: { api: Api }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [flow, setFlow] = useState<Flow>({ kind: "chat" });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Seed a greeting the first time the panel is opened.
  useEffect(() => {
    if (open && messages.length === 0) setMessages([msg("bot", GREETING)]);
  }, [open, messages.length]);

  // Keep the transcript pinned to the latest message and focus the input when opened.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open, flow]);

  function push(m: ChatMessage) {
    setMessages((prev) => [...prev, m]);
  }

  function beginCaseFlow(seedSubject: string) {
    if (seedSubject) {
      setFlow({ kind: "await_description", subject: seedSubject });
      push(
        msg(
          "bot",
          `Let's open a case: “${seedSubject}”.\nDescribe the issue in as much detail as you can — build version, OS, what you expected vs. what happened.`,
        ),
      );
    } else {
      setFlow({ kind: "await_subject" });
      push(msg("bot", "Sure — what's the subject of your case? (a short summary)"));
    }
  }

  async function submitCase(subject: string, description: string) {
    setBusy(true);
    try {
      const { ticket } = await api.createTicket({
        subject,
        description,
        priority: "normal",
        category: "other",
      });
      push(
        msg(
          "bot",
          `Done — I opened case “${ticket.subject}” (status: ${ticket.status}). Our team will follow up by email. You can track it on the Tickets tab.`,
          ticket.id,
        ),
      );
    } catch {
      push(
        msg(
          "bot",
          "I couldn't open the case just now. Please try again, or use the Tickets tab to create one directly.",
        ),
      );
    } finally {
      setFlow({ kind: "chat" });
      setBusy(false);
    }
  }

  async function askAssistant(question: string) {
    setBusy(true);
    try {
      const { answer } = await api.askAi(question);
      push(
        msg(
          "bot",
          answer ??
            "The assistant is unavailable right now. You can browse the FAQ or open a case and our team will help.",
        ),
      );
    } catch {
      push(
        msg("bot", "Something went wrong reaching the assistant. Please try again in a moment."),
      );
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    const text = input.trim();
    if (text === "" || busy) return;
    push(msg("user", text));
    setInput("");

    if (flow.kind === "await_subject") {
      beginDescription(text);
      return;
    }
    if (flow.kind === "await_description") {
      await submitCase(flow.subject, text);
      return;
    }
    // chat mode: route to case flow on intent, otherwise ask the assistant.
    if (detectCreateCaseIntent(text)) {
      beginCaseFlow(extractCaseSubject(text));
      return;
    }
    await askAssistant(text);
  }

  function beginDescription(subject: string) {
    setFlow({ kind: "await_description", subject });
    push(
      msg(
        "bot",
        "Got it. Now describe the issue in as much detail as you can — build version, OS, and what you expected vs. what happened.",
      ),
    );
  }

  function cancelCase() {
    setFlow({ kind: "chat" });
    push(msg("bot", "No problem — cancelled. Ask me anything else."));
  }

  const inCaseFlow = flow.kind === "await_subject" || flow.kind === "await_description";
  const placeholder =
    flow.kind === "await_subject"
      ? "Subject of your case…"
      : flow.kind === "await_description"
        ? "Describe the issue…"
        : "Ask a question…";

  return (
    <>
      {/* Launcher */}
      <button
        type="button"
        aria-label={open ? "Close help assistant" : "Open help assistant"}
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-accent text-white shadow-lg transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-accent/50"
      >
        {open ? <IconClose /> : <IconChat />}
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-24 right-5 z-50 flex h-[32rem] w-[22rem] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-lg border border-line bg-bg shadow-2xl">
          <div className="flex items-center justify-between border-b border-line bg-bg-soft px-4 py-3">
            <div>
              <div className="text-sm font-semibold text-ink">PumaMesh assistant</div>
              <div className="text-[11px] text-ink-dim">Quick help · open a case</div>
            </div>
            <button
              type="button"
              aria-label="Close help assistant"
              onClick={() => setOpen(false)}
              className="text-ink-dim hover:text-ink"
            >
              <IconClose />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {messages.map((m) => (
              <div key={m.id} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div
                  className={`max-w-[85%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm ${
                    m.role === "user"
                      ? "bg-accent text-white"
                      : "border border-line bg-bg-soft text-ink"
                  }`}
                >
                  {m.text}
                  {m.ticketId && (
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        navigate(`/app/tickets?ticket=${encodeURIComponent(m.ticketId!)}`);
                      }}
                      className="mt-2 block text-xs font-medium text-accent-strong underline"
                    >
                      View in Tickets →
                    </button>
                  )}
                </div>
              </div>
            ))}
            {busy && <div className="text-xs text-ink-dim">…</div>}
          </div>

          <div className="border-t border-line px-3 py-3">
            {!inCaseFlow ? (
              <button
                type="button"
                onClick={() => beginCaseFlow("")}
                disabled={busy}
                className="mb-2 w-full rounded-sm border border-line px-3 py-1.5 text-xs font-medium text-ink-muted hover:text-ink disabled:opacity-60"
              >
                + Create a case
              </button>
            ) : (
              <button
                type="button"
                onClick={cancelCase}
                disabled={busy}
                className="mb-2 w-full rounded-sm border border-line px-3 py-1.5 text-xs font-medium text-ink-muted hover:text-ink disabled:opacity-60"
              >
                Cancel case
              </button>
            )}
            <div className="flex items-end gap-2">
              <input
                ref={inputRef}
                className="input"
                type="text"
                value={input}
                placeholder={placeholder}
                disabled={busy}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void send();
                }}
              />
              <button
                type="button"
                className="btn-primary shrink-0"
                onClick={() => void send()}
                disabled={busy || input.trim() === ""}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function IconChat() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.9-.9L3 21l1.9-5.6a8.5 8.5 0 0 1-.9-3.9A8.38 8.38 0 0 1 12.5 3 8.38 8.38 0 0 1 21 11.5z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconClose() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6 6l12 12M18 6L6 18"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}
